import VIconButton from '@voly/ui-components/src/v2/VIconButton.jsx';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import VText from '@voly/ui-components/src/v2/VText.jsx';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon.jsx';
import { ReactElement } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import BulkActionButton from '../molecules/BulkActionButton';
import BulkEdit, { TBulkEditProps } from './BulkEdit';

const MAX_DISPLAYED_COUNT = 200;
const COUNT_TILE_SIZE = '16';
const DESCRIPTION_WIDTH = '40';
const SLIDE_UP_ANIMATION = 'slide-from-bottom, fade-in';
const ACTION_GAP = 5;
const HOST_Z_INDEX = 'var(--floating-actions-z-index)';

export type TBulkChangesBarProps = {
  selectedCount: number;
  edit: TBulkEditProps | undefined;
  onCopy: (() => void) | undefined;
  onMove: (() => void) | undefined;
  onExport: () => void;
  exportDisabledReason: string | undefined;
  onDelete: (() => void) | undefined;
  onClose: () => void;
};

const formatSelectedCount = (selectedCount: number): string => {
  if (selectedCount > MAX_DISPLAYED_COUNT) {
    return `${MAX_DISPLAYED_COUNT}+`;
  }

  return String(selectedCount);
};

const getDescriptionKey = (selectedCount: number): string => {
  if (selectedCount > 1) {
    return 'points_selected';
  }

  return 'point_selected';
};

const BulkChangesBar = ({
  selectedCount,
  edit,
  onCopy,
  onMove,
  onExport,
  exportDisabledReason,
  onDelete,
  onClose,
}: TBulkChangesBarProps): ReactElement => (
  <VStackRow
    role="toolbar"
    aria-label={translate(getDescriptionKey(selectedCount))}
    gap="6"
    width="fit-content"
    marginX="auto"
    paddingRight="4"
    overflow="hidden"
    borderRadius="l3"
    bg="bg.panel"
    boxShadow="lg"
    position="relative"
    zIndex={HOST_Z_INDEX}
    pointerEvents="auto"
    animationName={SLIDE_UP_ANIMATION}
    animationDuration="slow"
  >
    <VStackRow
      justifyContent="center"
      minWidth={COUNT_TILE_SIZE}
      height={COUNT_TILE_SIZE}
      paddingX="2"
      bg="primary.solid"
      color="fg.inverted"
      fontSize="3xl"
      fontWeight="semibold"
    >
      {formatSelectedCount(selectedCount)}
    </VStackRow>
    <VText width={DESCRIPTION_WIDTH} color="fg.muted" fontSize="lg" fontWeight="semibold">
      {translate(getDescriptionKey(selectedCount))}
    </VText>
    <VStackRow gap={ACTION_GAP}>
      {edit ? <BulkEdit {...edit} /> : null}
      {onCopy ? (
        <BulkActionButton icon="contentCopy" label={translate('copy')} onClick={onCopy} />
      ) : null}
      {onMove ? (
        <BulkActionButton icon="driveFileMove" label={translate('move')} onClick={onMove} />
      ) : null}
      <BulkActionButton
        icon="download"
        label={translate('export')}
        disabledReason={exportDisabledReason}
        onClick={onExport}
      />
      {onDelete ? (
        <BulkActionButton icon="delete" label={translate('delete')} onClick={onDelete} />
      ) : null}
    </VStackRow>
    <VIconButton
      aria-label={translate('deselect_all')}
      variant="ghost"
      size="sm"
      color="fg.muted"
      borderRadius="full"
      onClick={onClose}
    >
      <VMaterialIcon name="close" />
    </VIconButton>
  </VStackRow>
);

export default BulkChangesBar;
